/**
 * Sports, leagues, and the things about them the game rules depend on.
 *
 * The app began as football-only, and two football assumptions were spread
 * across the codebase: that a game has exactly four quarters, and that ESPN
 * lives under `/sports/football`. Basketball breaks both -- college games are
 * two halves, and ESPN numbers a basketball season by the year it *ends* --
 * so everything that varies by league is gathered here rather than being
 * rediscovered at each call site.
 */

import { floorMoney, roundMoney } from './money';

export type Sport = 'football' | 'basketball';

/**
 * Leagues the app can build a pool from.
 *
 * `NCAAF` was called `NCAA` when college football was the only college sport.
 * Stored values are migrated, but `normalizeLeague` still accepts the old
 * spelling: it can appear in the state of a game created before the rename,
 * which lives in Durable Object storage rather than in a table a migration
 * can reach.
 */
export type League = 'NFL' | 'NCAAF' | 'NBA' | 'NCAAM';

export interface LeagueMeta {
  league: League;
  sport: Sport;
  /** ESPN path segment pair, e.g. "football/nfl". */
  path: string;
  /** Short name, as shown on buttons and badges. */
  label: string;
  /** What the league is, for the sport picker. */
  description: string;
  /**
   * Scoring checkpoints in a game: four quarters, or two halves.
   *
   * This is the number of times the board pays out in `standard` mode, so it
   * decides the shape of `scores`, `locked` and `payouts.standard`.
   */
  periodCount: number;
  periodNoun: 'quarter' | 'half';
  /**
   * ESPN group id for the top division of a college league: FBS, or Division
   * I. Null for the pro leagues, which have no divisions to filter.
   *
   * Serves two purposes with the same number -- narrowing the scoreboard,
   * which otherwise returns every division, and walking the conference tree
   * when syncing the team library.
   */
  groupId: number | null;
  /** Whether this is a college league, which ESPN shapes differently. */
  collegiate: boolean;
  /** Prefix for team ids in our own library, e.g. "nfl-12". */
  teamIdPrefix: string;
  /** Stored on each team row, for display. */
  classification: string;
  /**
   * True when ESPN labels a season by the year it ends -- basketball's
   * 2026-27 season is `year: 2027`. Football uses the year it starts.
   */
  seasonEndsInYear: boolean;
  /**
   * Roughly how many times a game changes its score line.
   *
   * Counted from ESPN play data rather than estimated: an NFL game produced
   * eight distinct score lines, college basketball 87-98, and the NBA 121-143.
   * The figures here sit above those samples, because a board that runs out of
   * money mid-game is worse than one that leaves a little for the final twist.
   *
   * Only `everyscore` mode uses this, and it is why one per-score default
   * cannot serve both sports -- the same pot has to stretch ten to fifteen
   * times further in basketball.
   */
  expectedScoreChanges: number;
  /**
   * Whether `everyscore` mode is offered for this league.
   *
   * It pays on every change of the score line, which the admin logs by hand.
   * Football changes its score eight to fourteen times a game, which is a
   * pleasant number of moments. Basketball does it over a hundred times --
   * once every twenty seconds or so for two and a half hours -- and no
   * administrator is going to sit through that. The money can be scaled to
   * fit; the human cannot.
   */
  allowsEveryScore: boolean;
}

export const LEAGUES: Record<League, LeagueMeta> = {
  NFL: {
    league: 'NFL',
    sport: 'football',
    path: 'football/nfl',
    label: 'NFL',
    description: 'Pro football',
    periodCount: 4,
    periodNoun: 'quarter',
    groupId: null,
    collegiate: false,
    teamIdPrefix: 'nfl',
    expectedScoreChanges: 14,
    allowsEveryScore: true,
    classification: 'pro',
    seasonEndsInYear: false,
  },
  NCAAF: {
    league: 'NCAAF',
    sport: 'football',
    path: 'football/college-football',
    label: 'College Football',
    description: 'FBS',
    periodCount: 4,
    periodNoun: 'quarter',
    groupId: 80,
    collegiate: true,
    // Kept as "ncaa-" rather than renamed with the league: every team row and
    // every cached schedule is keyed by it, and a rename would churn the lot
    // to no benefit.
    teamIdPrefix: 'ncaa',
    expectedScoreChanges: 14,
    allowsEveryScore: true,
    classification: 'fbs',
    seasonEndsInYear: false,
  },
  NBA: {
    league: 'NBA',
    sport: 'basketball',
    path: 'basketball/nba',
    label: 'NBA',
    description: 'Pro basketball',
    periodCount: 4,
    periodNoun: 'quarter',
    groupId: null,
    collegiate: false,
    teamIdPrefix: 'nba',
    expectedScoreChanges: 150,
    allowsEveryScore: false,
    classification: 'pro',
    seasonEndsInYear: true,
  },
  NCAAM: {
    league: 'NCAAM',
    sport: 'basketball',
    path: 'basketball/mens-college-basketball',
    label: "College Basketball",
    description: 'Division I men',
    // College basketball is two halves, not four quarters. This is the one
    // place the difference is declared; everything else reads it from here.
    periodCount: 2,
    periodNoun: 'half',
    groupId: 50,
    collegiate: true,
    teamIdPrefix: 'ncaam',
    expectedScoreChanges: 110,
    allowsEveryScore: false,
    classification: 'd1',
    seasonEndsInYear: true,
  },
};

/** Every league, in the order the picker offers them. */
export const LEAGUE_ORDER: League[] = ['NFL', 'NCAAF', 'NBA', 'NCAAM'];

/**
 * Coerces a stored league string to a current one.
 *
 * Unknown values fall back to NFL rather than throwing: a game whose league
 * cannot be read is still a board full of people's squares, and refusing to
 * load it would be a far worse outcome than showing the wrong badge.
 */
export function normalizeLeague(raw: string | null | undefined): League {
  if (raw === 'NCAA') return 'NCAAF';
  if (raw && raw in LEAGUES) return raw as League;
  return 'NFL';
}

export function leagueMeta(league: string | null | undefined): LeagueMeta {
  return LEAGUES[normalizeLeague(league)];
}

export function sportOf(league: string | null | undefined): Sport {
  return leagueMeta(league).sport;
}

const QUARTER_LABELS = ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'];
const HALF_LABELS = ['1st Half', '2nd Half'];

/** Names for each scoring checkpoint, e.g. ["1st Half", "2nd Half"]. */
export function periodLabels(league: string | null | undefined): string[] {
  const meta = leagueMeta(league);
  const source = meta.periodNoun === 'half' ? HALF_LABELS : QUARTER_LABELS;
  return source.slice(0, meta.periodCount);
}

/**
 * Label for one checkpoint. Anything past the scheduled periods is overtime,
 * which both sports reach and neither schedules.
 */
export function periodLabel(league: string | null | undefined, index: number): string {
  return periodLabels(league)[index] ?? 'Overtime';
}

/**
 * Compact checkpoint name for tight spaces: "Q1", "H2", "OT".
 *
 * `index` is zero-based like the rest of the app; the feed's own period
 * numbers are one-based, so convert before calling.
 */
export function shortPeriodLabel(league: string | null | undefined, index: number): string {
  const meta = leagueMeta(league);
  if (index >= meta.periodCount) return 'OT';
  return `${meta.periodNoun === 'half' ? 'H' : 'Q'}${index + 1}`;
}

/**
 * Default payout per checkpoint, in dollars, growing towards the end.
 *
 * Four quarters take the 10/20/30/40 split the app has always used. Two halves
 * take 40/60 -- the same idea, that the last checkpoint is worth the most,
 * expressed over half as many payouts.
 */
export function defaultPayoutSplit(pot: number, periodCount: number): number[] {
  const weights =
    periodCount === 2 ? [0.4, 0.6] : [0.1, 0.2, 0.3, 0.4];
  const used = weights.slice(0, periodCount);
  const out = used.map((w) => roundMoney(pot * w));
  // Rounding must not conjure or lose money: the last checkpoint absorbs the
  // difference, since it is the one people check against the pot.
  const drift = roundMoney(pot - out.reduce((sum, n) => sum + n, 0));
  if (out.length > 0) out[out.length - 1] = roundMoney(out[out.length - 1]! + drift);
  return out;
}

/**
 * Path to a league on ESPN's core API, which nests leagues one level deeper
 * than the site API: "football/leagues/college-football".
 *
 * Only the college leagues need it, for the conference tree.
 */
export function coreLeaguePath(league: string | null | undefined): string {
  const meta = leagueMeta(league);
  const segment = meta.path.split('/')[1] ?? '';
  return `${meta.sport}/leagues/${segment}`;
}

/**
 * Default payout per score in `everyscore` mode.
 *
 * Derived from the pot rather than fixed, because a fixed figure is only ever
 * right for one board. The old default of $400 came from the original app and
 * assumed nothing: on a $20 board it exhausted the pot after five scores, so
 * the mode stopped paying in the second quarter and the final twist never
 * fired at all. On a penny board it would have paid nothing from the start.
 *
 * Never rounds to zero: a board that pays nothing per score is not playable.
 */
export function defaultPerScore(pot: number, league: string | null | undefined): number {
  // Floored, not rounded: a per-score a fraction of a cent too high covers one
  // fewer score than intended, which is the whole point of the figure.
  const per = floorMoney(pot / leagueMeta(league).expectedScoreChanges);
  return Math.max(0.01, per);
}

/** How many scores a given per-score payout covers before the pot is spent. */
export function scoresCovered(pot: number, perScore: number): number {
  if (!(perScore > 0)) return 0;
  return Math.floor(roundMoney(pot) / perScore);
}

/** Whether this league may run `everyscore` mode. */
export function allowsEveryScore(league: string | null | undefined): boolean {
  return leagueMeta(league).allowsEveryScore;
}
